import { z } from 'zod';
import {
  AI_CAPABILITIES,
  APPROVAL_INTENTS,
  APPROVAL_STATES,
  CONTEXT_SECTIONS,
  CRITERION_OUTCOMES,
  DEPLOYMENT_OUTCOMES,
  PRIORITIES,
  RELEASE_STATES,
  RESOLUTIONS,
  ROLES,
  TEST_RESULTS,
  TICKET_STATUSES,
  TICKET_TYPES,
} from './enums';
import { PROJECT_KEY_PATTERN } from './keys';

const text = (max: number) => z.string().trim().max(max);
const required = (max: number, label: string) => z.string().trim().min(1, `${label} is required`).max(max);
export const id = z.string().uuid();
export const version = z.number().int().nonnegative();
const reason = required(2000, 'Reason');
const markdown = z.string().max(100_000);

// ── Identity & administration ───────────────────────────────────────────────
export const createInvitationInput = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  role: z.enum(ROLES),
  projectIds: z.array(id).max(200).default([]),
});
export const updateUserInput = z.object({
  role: z.enum(ROLES).optional(),
  active: z.boolean().optional(),
  expectedVersion: version,
  reason: text(2000).optional(),
});
export const setMembershipsInput = z.object({ projectIds: z.array(id).max(500) });

// ── Projects ────────────────────────────────────────────────────────────────
export const createProjectInput = z.object({
  name: required(120, 'Name'),
  key: z
    .string()
    .trim()
    .toUpperCase()
    .regex(PROJECT_KEY_PATTERN, 'Key must be 2–10 uppercase letters or digits, starting with a letter'),
  description: text(5000).default(''),
});
export const updateProjectInput = z.object({
  name: required(120, 'Name'),
  description: text(5000),
  expectedVersion: version,
});
export const archiveProjectInput = z.object({ expectedVersion: version, reason: text(2000).optional() });
export const memberInput = z.object({ userId: id });

export const saveContextInput = z.object({
  body: markdown,
  changeSummary: required(500, 'Change summary'),
  /** Optimistic concurrency against the current version (null when no version exists). */
  expectedCurrentVersionId: id.nullable(),
});

export const setRepositoryInput = z.object({
  url: required(2000, 'Repository URL'),
  defaultBranch: required(255, 'Default branch'),
  reason: text(2000).optional(),
});
export const builderInput = z.object({
  name: required(120, 'Name'),
  machineId: required(255, 'Machine identifier'),
  basePath: required(1024, 'Base checkout path'),
  description: text(2000).default(''),
});
export const updateBuilderInput = builderInput.extend({ active: z.boolean(), expectedVersion: version });
export const builderHealthInput = z.object({
  status: z.enum(['healthy', 'degraded', 'unreachable']),
  note: text(1000).default(''),
});
export const environmentInput = z.object({
  name: required(80, 'Name'),
  description: text(2000).default(''),
  isProduction: z.boolean().default(false),
  verificationExpectations: text(10_000).default(''),
  rollbackInstructions: text(10_000).default(''),
});
export const updateEnvironmentInput = environmentInput.extend({ expectedVersion: version });

// ── AI services ─────────────────────────────────────────────────────────────
export const createAiServiceInput = z.object({
  name: required(120, 'Name'),
  description: text(2000).default(''),
});
/** Activation, and for headless agents also the display name and description. */
export const updateAiServiceInput = z
  .object({
    active: z.boolean().optional(),
    name: required(120, 'Name').optional(),
    description: text(2000).optional(),
    expectedVersion: version,
  })
  .refine((v) => v.active !== undefined || v.name !== undefined || v.description !== undefined, {
    message: 'Change at least one of active, name or description.',
  });
export const issueCredentialInput = z.object({
  label: required(120, 'Label'),
  expiresInDays: z.number().int().min(1).max(365),
});
export const setGrantsInput = z.object({ projectId: id, capabilities: z.array(z.enum(AI_CAPABILITIES)) });

// ── Tickets ─────────────────────────────────────────────────────────────────
export const acceptanceCriteria = z.array(required(2000, 'Criterion')).max(50);
export const createTicketInput = z.object({
  type: z.enum(TICKET_TYPES),
  title: required(200, 'Title'),
  description: markdown.refine((s) => s.trim().length > 0, 'Description is required'),
  acceptanceCriteria: acceptanceCriteria.default([]),
  stepsOrOutcome: text(20_000).default(''),
  labels: z.array(required(40, 'Label')).max(20).default([]),
  priority: z.enum(PRIORITIES).optional(),
  parentKey: z.string().optional(),
});
export const editScopeInput = z.object({
  type: z.enum(TICKET_TYPES),
  title: required(200, 'Title'),
  description: markdown.refine((s) => s.trim().length > 0, 'Description is required'),
  acceptanceCriteria,
  stepsOrOutcome: text(20_000),
  expectedVersion: version,
  /** Admin material edit of accepted scope must explicitly confirm return to pending (WF-04). */
  confirmReturnToPending: z.boolean().default(false),
  reason: text(2000).optional(),
});
export const triageInput = z.object({
  priority: z.enum(PRIORITIES).optional(),
  assigneeUserId: id.nullable().optional(),
  labels: z.array(required(40, 'Label')).max(20).optional(),
  expectedVersion: version,
});
export const acceptInput = z.object({
  expectedVersion: version,
  scopeRevision: z.number().int().positive(),
  approvalRequestId: id.optional(),
});
export const reasonInput = z.object({ expectedVersion: version, reason });
export const reworkInput = z.object({
  expectedVersion: version,
  reason,
  target: z.enum(['todo', 'in_progress']),
});
export const setParentInput = z.object({ parentKey: z.string().nullable(), expectedVersion: version });
export const linkInput = z.object({ targetKey: required(40, 'Ticket'), expectedVersion: version });
export const blockerInput = z.object({ reason });
export const overrideInput = z.object({ reason });
export const scopeDecisionInput = z.object({
  childTicketKey: required(40, 'Child ticket'),
  decision: reason,
});
export const clarificationInput = z.object({ needed: z.boolean(), reason: text(2000).default('') });

export const commentInput = z.object({
  body: markdown.refine((s) => s.trim().length > 0, 'Comment is empty'),
});
export const editCommentInput = commentInput.extend({ expectedVersion: version });
export const moderateInput = z.object({ reason: text(2000).optional() });

export const ticketFilters = z.object({
  q: text(200).optional(),
  projectKey: z.string().optional(),
  status: z.array(z.enum(TICKET_STATUSES)).optional(),
  resolution: z.array(z.enum(RESOLUTIONS)).optional(),
  type: z.array(z.enum(TICKET_TYPES)).optional(),
  priority: z.array(z.enum(PRIORITIES)).optional(),
  assignee: z.string().optional(),
  reporter: z.string().optional(),
  label: z.string().optional(),
  blocked: z.boolean().optional(),
  releaseId: id.optional(),
  createdFrom: z.string().optional(),
  createdTo: z.string().optional(),
  updatedSince: z.string().optional(),
  includeArchived: z.boolean().optional(),
  sort: z.enum(['updated', 'created', 'priority', 'number']).default('updated'),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type TicketFilters = z.infer<typeof ticketFilters>;

// ── Approvals ───────────────────────────────────────────────────────────────
export const approvalFilters = z.object({
  projectKey: z.string().optional(),
  intent: z.enum(APPROVAL_INTENTS).optional(),
  state: z.enum(APPROVAL_STATES).optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export const requestAcceptanceInput = z.object({
  ticketKey: required(40, 'Ticket'),
  scopeRevision: z.number().int().positive(),
  rationale: text(10_000).default(''),
  discussionLink: text(2000).optional(),
  outstandingQuestions: text(10_000).optional(),
});
export const decideApprovalInput = z.object({
  decision: z.enum(['approve', 'deny']),
  /** Snapshot hash the admin reviewed; a changed snapshot blocks the decision. */
  snapshotHash: required(128, 'Snapshot'),
  reason: text(2000).optional(),
});

// ── Implementation ──────────────────────────────────────────────────────────
export const claimInput = z.object({
  ticketKey: required(40, 'Ticket'),
  expectedVersion: version,
  acceptanceId: id,
  builderId: id,
});
export const leaseRef = z.object({
  ticketKey: required(40, 'Ticket'),
  leaseToken: required(200, 'Lease token'),
  generation: z.number().int().positive(),
});
export const progressInput = leaseRef.extend({
  expectedVersion: version,
  phase: text(120).optional(),
  note: text(10_000).optional(),
  branch: text(255).optional(),
  baseRevision: text(64).optional(),
  worktree: text(1024).optional(),
  workDirectory: text(1024).optional(),
  blocker: text(2000).optional(),
});
export const evidenceInput = z.object({
  summary: text(20_000),
  sourceRevision: text(64),
  branch: text(255).nullish(),
  pullRequest: text(2000).nullish(),
  worktree: text(1024).nullish(),
  criteriaResults: z
    .array(
      z.object({
        criterionIndex: z.number().int().min(0),
        outcome: z.enum(CRITERION_OUTCOMES),
        observation: text(5000),
      }),
    )
    .max(50),
  tests: z
    .array(
      z.object({
        name: required(200, 'Test name'),
        command: text(2000),
        result: z.enum(TEST_RESULTS),
        evidenceRef: text(2000).nullish(),
      }),
    )
    .max(200),
  knownLimitations: text(10_000).default(''),
  deploymentConsiderations: text(10_000),
  rollbackConsiderations: text(10_000),
  risks: text(10_000).nullish(),
  contextVersions: z.partialRecord(z.enum(CONTEXT_SECTIONS), id.nullable()).default({}),
});
export const recordEvidenceInput = leaseRef.extend({ expectedVersion: version, evidence: evidenceInput });
export const markReadyInput = leaseRef.extend({ expectedVersion: version });
export const releaseClaimInput = leaseRef.extend({ expectedVersion: version, reason });
export const manualEvidenceInput = z.object({ expectedVersion: version, evidence: evidenceInput });
export const manualReadyInput = z.object({ expectedVersion: version });
export const recoverInput = z.object({
  expectedVersion: version,
  choice: z.enum(['manual', 'todo', 'pending']),
  workspaceAssessment: required(5000, 'Workspace assessment'),
  reason,
  assigneeUserId: id.nullable().optional(),
});
export const exceptionInput = z.object({ testName: required(200, 'Test name'), reason });

// ── Releases ────────────────────────────────────────────────────────────────
export const releaseCandidateInput = z.object({
  title: required(200, 'Title'),
  environmentId: id,
  ticketKeys: z.array(required(40, 'Ticket')).min(1).max(200),
  sourceRevision: required(64, 'Source revision'),
  artifactDigest: text(255).nullish(),
  artifactRef: text(2000).nullish(),
  integrationEvidence: required(20_000, 'Integration evidence'),
  deploymentPlan: required(20_000, 'Deployment plan'),
  verificationChecks: z.array(required(500, 'Check')).min(1).max(50),
  /**
   * Verification checks a person records after a successful Deploy run (each must also be listed in
   * verificationChecks). The other checks count as covered by the run itself.
   */
  manualChecks: z.array(required(500, 'Check')).max(50).optional(),
  rollbackPlan: required(20_000, 'Rollback plan'),
  executor: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('human'), userId: id }),
    z.object({ kind: z.literal('ai_service'), serviceId: id }),
  ]),
});
export const createReleaseInput = releaseCandidateInput.extend({ projectKey: required(20, 'Project') });
export const updateReleaseInput = releaseCandidateInput.extend({ expectedVersion: version });
export const requestReleaseApprovalInput = z.object({
  expectedVersion: version,
  rationale: text(10_000).default(''),
});
export const beginAttemptInput = z.object({ expectedVersion: version, approvalRequestId: id });
export const deploymentReportInput = z.object({
  expectedVersion: version,
  sourceRevision: required(64, 'Source revision'),
  artifactDigest: text(255).nullish(),
  environmentId: id,
  outcome: z.enum(DEPLOYMENT_OUTCOMES),
  startedAt: z.string().datetime({ offset: true }).optional(),
  finishedAt: z.string().datetime({ offset: true }).optional(),
  deploymentReference: text(2000).nullish(),
  log: text(100_000).nullish(),
  checks: z
    .array(
      z.object({ name: required(500, 'Check'), passed: z.boolean(), observation: text(5000).default('') }),
    )
    .max(50),
  evidenceRefs: z.array(text(2000)).max(50).default([]),
  partialEffects: text(10_000).nullish(),
  errors: text(10_000).nullish(),
  recoveryActions: text(10_000).nullish(),
});
export const verifyInput = z.object({ reason, evidence: required(10_000, 'Evidence') });
/** An admin's result for one manual check of an attempt whose Deploy run succeeded. */
export const manualCheckInput = z.object({
  expectedVersion: version,
  name: required(500, 'Check'),
  passed: z.boolean(),
  observation: text(5000).default(''),
});
export const correctionInput = z.object({
  reason,
  correctedOutcome: z.enum(DEPLOYMENT_OUTCOMES),
  evidence: required(10_000, 'Evidence'),
});
export const reconcileInput = z.object({
  outcome: z.enum(['success', 'failure']),
  reason,
  evidence: required(10_000, 'Evidence'),
  checks: deploymentReportInput.shape.checks.default([]),
});
export const prepareRollbackInput = z.object({
  title: required(200, 'Title'),
  environmentStateObservation: required(10_000, 'Current environment state'),
  rollbackRevision: required(64, 'Rollback revision'),
  artifactDigest: text(255).nullish(),
  verificationChecks: z.array(required(500, 'Check')).min(1).max(50),
  plan: required(20_000, 'Plan'),
  affectedTicketKeys: z.array(required(40, 'Ticket')).max(200),
  executor: releaseCandidateInput.shape.executor,
});
export const releaseFilters = z.object({
  state: z.enum(RELEASE_STATES).optional(),
  environmentId: id.optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
